import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ListFilter from "./listFilter";
import Button from "../../../components/buttons/buttons";
import Fail from "../../../assets/images/icons/failed-txn.svg";
import Success from "../../../assets/images/icons/success-txn.svg";
import Pending from "../../../assets/images/icons/pending-txn.svg";
import Empty from "../../../assets/images/emptyState/invites.png";
import TableSkeleton from "../../../components/skeleton/table";
import { AllFiltersModal } from "../../../components/modals/allFiltersModal";
import { PaginationNoRoute } from "../../../components/pagination/paginationNoRoute";
import RetryPaymentModal from "../../../components/modals/payment/retryPaymentModal";
import ViewPaymentModal from "../../../components/modals/payment/viewPaymentModal";
import { searchMyPayments, filterMyPayment } from "../../../redux/payment/paymentActions";
import paymentType from "../../../redux/payment/paymentTypes";
import FilterUserPaymentsDropDown from "../../../components/dropDown/filter/filterUserPayments";
import { Beat } from "../../../plugins/spinners.plugin";
import { writeFileWithXLSX } from "../../../components/exports/xlsx";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export default function MyPayments() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const retryPaymentModalRef = useRef(null);
  const viewPaymentModalRef = useRef(null);
  const [page, setPage] = useState(1);
  const [isFilterOpen, setFilterState] = useState(false);
  const [input, setInput] = useState({ search: "" });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [payLoad, setPayLoad] = useState({ end_date: new Date() });
  const [data, setData] = useState(null);
  const { myPayments, search, isLoading, searching, downloading } = useSelector((state) => state.payment);

  // Use search results if searching, otherwise use myPayments
  const allPayments = isSearchOpen ? { data: search } : myPayments;

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    dispatch(filterMyPayment(payLoad, page));
    // eslint-disable-next-line
  }, [page]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  const removeSearch = () => {
    if (input?.search?.length === 0) {
      setIsSearchOpen(false);
      dispatch({
        type: paymentType["SEARCH_PAYMENT"],
        payLoad: { data: [] },
      });
    }
  };

  const handleDownload = async () => {
    const response = await dispatch(
      filterMyPayment({
        ...payLoad,
        pagination: 0,
      })
    );
    if (response) {
      const newData = response?.data?.map((payment) => ({
        "Payment ID": payment?.id,
        "Reference": payment?.reference,
        "Amount": payment?.amount,
        "Status": payment?.paid ? "Success" : payment?.status === "pending" ? "Pending" : "Failed",
        "Payment Method": payment?.payment_method,
        "Created Date": moment(payment?.created_at).format("MMM DD, YYYY HH:mm"),
      }));
      writeFileWithXLSX(newData, {
        filename: "My_Payments.xlsx",
        download: true,
      });
    }
  };

  const showSearch = (search) => {
    setData(search);
    setIsSearchOpen(true);
  };

  async function doSearch() {
    if (input?.search?.trim().length > 0) {
      try {
        let response = await dispatch(searchMyPayments(input));
        if (response?.status === "success") {
          showSearch(response?.data);
        }
        if (!response?.status) {
          showSearch([]);
        }
      } catch (error) { }
    }
  }

  useEffect(() => {
    if (input?.search?.length === 0) {
      removeSearch();
    }
    // eslint-disable-next-line
  }, [input, dispatch]);

  const groupedData = allPayments?.data?.reduce((acc, obj) => {
    const d = new Date(obj.created_at);
    let month = months[d.getMonth()];
    let year = d.getFullYear();
    const key = `${month} ${year}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});

  return (
    <>
      <div className="sm:w-[820px] w-full mt-10 sm:ml-10 sm:mt-0 px-6 bg-white dark:bg-[#121212] min-h-screen rounded-sm shadow-2xl">
        <div className="border-b-2 py-4 sm:py-6 mb-6 block">
          <p className="text-base font-medium text-gray-800 dark:text-white text-center">
            My Payments
          </p>
        </div>

        <div>
          <div className="mb-4">
            <ListFilter
              results={allPayments?.to}
              totalResults={allPayments?.total}
              onChange={handleChange}
              handleKeyDown={handleKeyDown}
              searchPlaceholder={"Search for payment by payment id"}
              searchValue={input.search}
              filter={
                <AllFiltersModal
                  isFilterOpen={isFilterOpen}
                  setFilterState={setFilterState}
                >
                  <FilterUserPaymentsDropDown
                    isFilterOpen={isFilterOpen}
                    setFilterState={setFilterState}
                    payLoad={payLoad}
                    setPayLoad={setPayLoad}
                    page={page}
                  />
                </AllFiltersModal>
              }
            >
              <Button.Secondary
                disabled={!data && !allPayments?.data}
                title={downloading ? <Beat color={"#CE2600"} /> : "Download CSV"}
                onClick={handleDownload}
              />
            </ListFilter>
          </div>


          {(isLoading || searching) ? (
            <TableSkeleton />
          ) : allPayments?.data?.length > 0 ? (
            <>
              {Object.entries(groupedData).map(([monthYear, objs]) => (
                <div key={monthYear}>
                  <h1 className="text-xl font-medium py-6 uppercase ">
                    {monthYear}
                  </h1>
                  <div className="sm:border-b-2 dark:border-none"></div>
                  <ul className="sm:p-2">
                    {objs.map((obj, index) => (
                      <li
                        key={index}
                        className="border-b border-gray-300 dark:border-none grid grid-cols-3 sm:grid-cols-4 gap-2 items-center py-2"
                      >
                        <div className="hidden sm:flex text-base text-[#676565]">
                          <p>
                            {moment(obj?.created_at).format("MMM DD . HH:mm")}
                          </p>
                        </div>

                        {obj?.status === "pending" ? (
                          <div className="flex items-center gap-1">
                            <p className="text-[#FFA927] ml-1 text-sm sm:text-lg font-bold sm:font-medium sm:min-w-[100px]">
                              Pending
                            </p>
                            <img src={Pending} alt="icon" className="w-5 h-5" />
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            {obj?.paid ? (
                              <p className="text-[#00D220] ml-1 text-sm sm:text-lg font-bold sm:font-medium sm:min-w-[100px]">
                                Success
                              </p>
                            ) : (
                              <p className="text-primary ml-1 text-sm sm:text-lg font-bold sm:font-medium sm:min-w-[100px]">
                                Failed
                              </p>
                            )}
                            {obj?.paid ? (
                              <img
                                src={Success}
                                alt="icon"
                                className="w-5 h-5"
                              />
                            ) : (
                              <img src={Fail} alt="icon" className="w-5 h-5" />
                            )}
                          </div>
                        )}

                        <p className="text-base sm:text-lg font-medium">
                          ₦{numberWithCommas(obj?.amount)}
                        </p>

                        {obj?.paid || obj?.status === "pending" ? (
                          <Button.Secondary
                            title={"View"}
                            type="button"
                            className="px-0"
                            onClick={() => {
                              viewPaymentModalRef.current.open();
                              viewPaymentModalRef.current.getId(obj?.id);
                            }}
                          />
                        ) : (
                          <Button.Primary
                            title={"Retry"}
                            type="button"
                            className="px-0"
                            onClick={() => {
                              if (retryPaymentModalRef.current) {
                                retryPaymentModalRef.current.open();
                                retryPaymentModalRef.current.getPfm(obj?.pfm);
                                retryPaymentModalRef.current.getTotalPrice(
                                  obj?.amount
                                );
                                retryPaymentModalRef.current.getProviderPrice(
                                  obj
                                );
                                retryPaymentModalRef.current.getReference(
                                  obj?.id
                                );
                              }
                            }}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {!isSearchOpen && (
                <PaginationNoRoute
                  data={myPayments}
                  page={page}
                  setPage={setPage}
                />
              )}
            </>
          ) : (
            <div className="my-10 mt-20  text-center">
              <img
                src={Empty}
                alt="empty state"
                className="flex justify-center mx-auto mb-4"
              />
              <p className="font-bold mb-2">No Payments</p>
              <p className="text-sm text-gray-400">
                Your payment history shows here.
              </p>
            </div>
          )}
        </div>
        <Button.Secondary
          type="button"
          title={"Back"}
          className={"sm:hidden rounded-md mb-8 sm:mb-0 w-full mt-8"}
          onClick={() => navigate("/profile")}
        />
        <RetryPaymentModal ref={retryPaymentModalRef} visibility={false} />
        <ViewPaymentModal ref={viewPaymentModalRef} visibility={false} />
      </div>
    </>
  );
}
